import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, cx } from "./ui";
import { Icon } from "./icons";
import { absoluteTime, clockTime, dayHeading, formatBytes, localDayKey, relativeTime } from "../lib/format";
import type { BackupFile } from "../lib/types";

/**
 * A database's backups, as the points it can be restored to.
 *
 * A timeline rather than a table of file names, because the question somebody
 * brings here is "which moment do I want back" — and a moment reads as a day
 * and a time, not as `2026-09-22T02-37-01-004Z.sql.gz`. The name is still on
 * the row (its tooltip, and the restore dialog), because it is what support
 * and the API talk about.
 *
 * Days group the rows, newest first; the schedule's own backups and a
 * restore's safety backups sit in one line, told apart by their mark and their
 * words, because both are equally restorable and the safety one is often the
 * very one wanted — the undo.
 *
 * The actions stay visible rather than appearing on hover: a touch screen has
 * no hover, and an action that only exists under a pointer is one a person
 * has to already know is there.
 */

/** Rows shown before the list asks to be opened. Retention keeps up to 365. */
const FOLDED = 8;

export function RestorePoints({
  dumps,
  canRestore,
  canDownload,
  restoreBusy,
  downloading,
  onRestore,
  onDownload,
}: {
  dumps: BackupFile[];
  canRestore: boolean;
  canDownload: boolean;
  /** A restore into this database is running; another cannot start. */
  restoreBusy: boolean;
  /** The key whose link is being fetched, so only that button spins. */
  downloading: string | null;
  onRestore: (file: BackupFile) => void;
  onDownload: (key: string) => void;
}) {
  const { t } = useTranslation("database");
  const [open, setOpen] = useState(false);
  const shown = open ? dumps : dumps.slice(0, FOLDED);

  // Consecutive rows of one local day share a heading. The API answers newest
  // first, so a day's rows are already together.
  const days: { key: string; heading: string; rows: BackupFile[] }[] = [];
  for (const file of shown) {
    const key = localDayKey(file.date);
    const last = days[days.length - 1];
    if (last && last.key === key) last.rows.push(file);
    else days.push({ key, heading: dayHeading(file.date), rows: [file] });
  }

  return (
    <div>
      {days.map((day) => (
        <section key={day.key} aria-label={day.heading}>
          <h5 className="micro border-t border-line px-4 pt-3 pb-1.5 first:border-t-0">{day.heading}</h5>
          <ul>
            {day.rows.map((file) => (
              <li
                key={file.key}
                title={`${file.name} · ${absoluteTime(file.date)}`}
                className="group flex items-center gap-3 px-4 py-2 transition-colors duration-150 hover:bg-raised/40"
              >
                <span
                  aria-hidden
                  className={cx(
                    "flex size-7 shrink-0 items-center justify-center rounded-md border",
                    file.beforeRestore ? "border-accent/30 bg-accent-soft text-accent" : "border-line text-fg-subtle",
                  )}
                >
                  <Icon name={file.beforeRestore ? "shield" : "clock"} className="size-3.5" />
                </span>

                <div className="min-w-0 flex-1">
                  <p className="flex items-baseline gap-2 text-sm">
                    <span className="font-medium tabular-nums text-fg">{clockTime(file.date)}</span>
                    <span className="truncate text-fg-muted">
                      {file.beforeRestore ? t("points.safety") : t("points.scheduled")}
                    </span>
                  </p>
                  <p className="mt-0.5 truncate text-xs text-fg-subtle">
                    <span className="tabular-nums">{formatBytes(file.size)}</span>
                    {" · "}
                    {relativeTime(file.date)}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {canRestore && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="refresh"
                      disabled={restoreBusy}
                      title={restoreBusy ? t("restore.busy") : t("points.restoreThis")}
                      aria-label={t("points.restoreThis")}
                      onClick={() => onRestore(file)}
                    >
                      <span className="sr-only sm:not-sr-only">{t("restore.action")}</span>
                    </Button>
                  )}
                  {canDownload && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="download"
                      loading={downloading === file.key}
                      title={t("backups.download")}
                      aria-label={t("backups.download")}
                      onClick={() => onDownload(file.key)}
                    >
                      <span className="sr-only">{t("backups.download")}</span>
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {dumps.length > FOLDED && (
        <div className="border-t border-line px-4 py-2.5">
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? t("points.fewer") : t("points.all", { count: dumps.length })}
          </Button>
        </div>
      )}
    </div>
  );
}
